import React, { useState, useEffect } from 'react';
import { X, Info, Plus, ArrowUpDown, Check } from 'lucide-react';
import Banner from '../../components/layout/Banner';
import PageTitle from '../../components/layout/PageTitle';
import GoalCard from '../../components/features/GoalCard';
import { appFeatures } from '../../constants/features';
import { TOP_PRIORITY_COUNT } from '../../constants/goals';
import styles from './GoalsPage.module.css';

const GoalsPage = ({
	goals = [],
	onDeleteGoal,
	onTrackGoal,
	onViewCareTips,
	onWatchGoal,
	onLogoClick,
	onUpdateGoals, // Add this prop to handle navigation to UpdateGoalsPage
	onSavePriority, // Persist priority ranks (top 5 => 1..5, rest => 0)
}) => {
	// State for info overlay
	const [showInfoOverlay, setShowInfoOverlay] = useState(false);
	const [selectedGoalInfo, setSelectedGoalInfo] = useState(null);

	// Reorder / priority state (UI only — no API wiring yet)
	const [reorderMode, setReorderMode] = useState(false);
	const [orderedGoals, setOrderedGoals] = useState(goals);
	const [dragIndex, setDragIndex] = useState(null);

	// Has the user set priorities before? (rank 0 / unset = never prioritized)
	const hasPriority = goals.some((g) => (g.priority_rank ?? 0) > 0);

	// Keep the local ordered copy in sync with incoming goals when not editing
	useEffect(() => {
		if (!reorderMode) {
			setOrderedGoals(goals);
		}
	}, [goals, reorderMode]);

	// Get the goals feature data for consistent styling
	const goalsFeature = appFeatures.find((feature) => feature.id === 'goals');

	const enterReorderMode = () => {
		setOrderedGoals(goals);
		setReorderMode(true);
	};

	const cancelReorder = () => {
		setOrderedGoals(goals);
		setReorderMode(false);
		setDragIndex(null);
	};

	const saveReorder = () => {
		// Top 5 get ranks 1..5; everything below the cutoff is 0 (not prioritized).
		const ranks = orderedGoals.map((goal, index) => ({
			goal_setting_id: goal.goal_setting_id,
			priority_rank: index < TOP_PRIORITY_COUNT ? index + 1 : 0,
		}));
		setReorderMode(false);
		setDragIndex(null);
		if (onSavePriority) {
			onSavePriority(ranks);
		}
	};

	const moveGoal = (from, to) => {
		if (to < 0 || to >= orderedGoals.length) return;
		setOrderedGoals((prev) => {
			const next = [...prev];
			const [moved] = next.splice(from, 1);
			next.splice(to, 0, moved);
			return next;
		});
	};

	const handleDragStart = (index) => setDragIndex(index);

	const handleDragOver = (index) => (e) => {
		e.preventDefault();
		if (dragIndex === null || dragIndex === index) return;
		moveGoal(dragIndex, index);
		setDragIndex(index);
	};

	const handleDragEnd = () => setDragIndex(null);

	const handleDeleteGoal = (goalId) => {
		if (onDeleteGoal) {
			onDeleteGoal(goalId);
		}
	};

	const handleTrackGoal = (goalId) => {
		if (onTrackGoal) {
			onTrackGoal(goalId);
		}
	};

	const handleViewInfo = (goalId) => {
		const goal = goals.find((g) => g.id === goalId);
		if (goal) {
			setSelectedGoalInfo(goal);
			setShowInfoOverlay(true);
		}
	};

	const handleCloseInfo = () => {
		setShowInfoOverlay(false);
		setSelectedGoalInfo(null);
	};

	const handleViewCareTips = (goalId) => {
		if (onViewCareTips) {
			onViewCareTips(goalId);
		}
	};

	const handleWatchGoal = (goalId) => {
		if (onWatchGoal) {
			onWatchGoal(goalId);
		}
	};

	const handleUpdateGoals = () => {
		if (onUpdateGoals) {
			onUpdateGoals();
		}
	};

	// Handle overlay click (close when clicking outside the modal)
	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			handleCloseInfo();
		}
	};

	// Prevent scroll when overlay is open
	React.useEffect(() => {
		if (showInfoOverlay) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}

		// Cleanup on unmount
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [showInfoOverlay]);

	return (
		<div className={styles.container}>
			{/* Banner with eCare-PD logo */}
			<Banner onLogoClick={onLogoClick} />

			{/* Main content */}
			<div className={styles.content}>
				<div className={styles.hero}>
					<div className={styles.titleRow}>
						<PageTitle
							icon={goalsFeature.icon}
							title={goalsFeature.title}
							color={goalsFeature.color}
						/>

						{goals.length > 1 &&
							(reorderMode ? (
								<div className={styles.reorderActions}>
									<button
										className={styles.cancelReorderButton}
										onClick={cancelReorder}
									>
										Cancel
									</button>
									<button
										className={styles.doneReorderButton}
										onClick={saveReorder}
									>
										<Check size={16} />
										<span>Done</span>
									</button>
								</div>
							) : (
								<button
									className={styles.reorderButton}
									onClick={enterReorderMode}
									aria-label={
										hasPriority ? 'Adjust goal priority' : 'Set goal priority'
									}
								>
									<ArrowUpDown size={16} />
									<span>{hasPriority ? 'Adjust Priority' : 'Set Priority'}</span>
								</button>
							))}
					</div>

					{reorderMode && (
						<p className={styles.reorderHint}>
							Drag or use the arrows to rank your goals. Only your top{' '}
							{TOP_PRIORITY_COUNT} priorities are actively tracked.
						</p>
					)}

					{/* Goals container */}
					<div
						className={`${styles.goalsContainer} ${
							reorderMode ? styles.goalsContainerReorder : ''
						}`}
					>
						{(reorderMode ? orderedGoals : goals).map((goal, index) => {
							// Reorder mode: every card shows a temporary 1..N rank.
							// Normal mode: only goals with a saved rank (> 0) show a badge.
							const savedRank = goal.priority_rank ?? 0;
							const displayRank = reorderMode
								? index + 1
								: savedRank > 0
								? savedRank
								: null;
							const isTop5 = reorderMode
								? index < TOP_PRIORITY_COUNT
								: savedRank > 0 && savedRank <= TOP_PRIORITY_COUNT;

							return (
							<React.Fragment key={goal.id}>
								{reorderMode && index === TOP_PRIORITY_COUNT && (
									<div className={styles.topDivider}>
										<span>Others</span>
									</div>
								)}
								<GoalCard
									goal={goal}
									rank={displayRank}
									isTop5={isTop5}
									reorderMode={reorderMode}
									isDragging={reorderMode && dragIndex === index}
									canMoveUp={index > 0}
									canMoveDown={index < orderedGoals.length - 1}
									onMoveUp={() => moveGoal(index, index - 1)}
									onMoveDown={() => moveGoal(index, index + 1)}
									dragHandleProps={
										reorderMode
											? {
													draggable: true,
													onDragStart: () => handleDragStart(index),
													onDragOver: handleDragOver(index),
													onDragEnd: handleDragEnd,
											  }
											: {}
									}
									onDelete={() => handleDeleteGoal(goal.id)}
									onTrack={() => handleTrackGoal(goal.id)}
									onViewInfo={() => handleViewInfo(goal.id)}
									onViewCareTips={() => handleViewCareTips(goal.id)}
									onWatch={
										goal.smart_watch ? () => handleWatchGoal(goal.id) : null
									}
								/>
							</React.Fragment>
							);
						})}

						{/* Add Goals Button Card — hidden while reordering */}
						{!reorderMode && (
							<div
								className={styles.addGoalsCard}
								onClick={handleUpdateGoals}
								role='button'
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										handleUpdateGoals();
									}
								}}
								aria-label='Add or update goals'
							>
								<div className={styles.addGoalsContent}>
									<div className={styles.addGoalsIcon}>
										<Plus size={32} />
									</div>
									<div className={styles.addGoalsText}>
										<h3 className={styles.addGoalsTitle}>Add Goals</h3>
										<p className={styles.addGoalsDescription}>
											Update your self-care goals and track your journey
										</p>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Info Overlay */}
			{showInfoOverlay && selectedGoalInfo && (
				<div className={styles.infoOverlay} onClick={handleOverlayClick}>
					<div className={styles.infoModal}>
						<div className={styles.infoHeader}>
							<div className={styles.infoIcon}>
								<Info size={24} color={goalsFeature.color} />
							</div>
							<h3 className={styles.infoTitle}>
								{selectedGoalInfo.name ||
									selectedGoalInfo.title ||
									'Goal Information'}
							</h3>
							<button
								className={styles.closeButton}
								onClick={handleCloseInfo}
								aria-label='Close information'
							>
								<X size={20} />
							</button>
						</div>
						<div className={styles.infoContent}>
							<p className={styles.infoDescription}>
								{selectedGoalInfo.short_description ||
									selectedGoalInfo.description ||
									'No additional information available for this goal.'}
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default GoalsPage;
